import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
//import { GridOptions, ColDef } from '@ag-grid-enterprise/all-modules';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { Subscription } from 'rxjs';
import { filter, map } from "rxjs/operators";

import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';

import { FinancialAssuranceService } from '../../../../common/services/financial-assurance.service';
import { OrganizationComplianceDetails } from "../../../../common/models/organization.model";
import { ExpiringFinancialInstrument, FinancialInstrumentType, FinancialInstrument, CertificateOfDeposit } from '../../../../common/models/financialAssurance.model';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { ActionCellRendererComponent } from '../../../../common/components/ag-grid/action-cell-renderer.component';
import { Action } from 'rxjs/internal/scheduler/Action';

export class ExpiringFinancialInstrumentListing {
    id: string;
    organizationId: string;
    organizationName: string;
    amount: number;
    instrumentId?: string;
    guarantor?: string;
    typeCode: FinancialInstrumentType;
    typeDescription: string;
    modifiedDate?: Date;
    expirationDate?: Date;
}

@Component({
    selector: 'internal-upcoming-bond-expiration-widget',
    templateUrl: './internal-upcoming-bond-expiration-widget.component.html',
    standalone: false
})
export class InternalUpcomingBondExpirationWidget implements OnInit, OnDestroy {

    @Output() companyClicked = new EventEmitter<string>();


    @Input() complianceData: OrganizationComplianceDetails[];
    @Input() widgetTitle: string;
    @Input() gridAriaLabel: string;

  timeframe: number = 30;
  private subscriptions = new Subscription();
  columnDefs : ColDef[] = [
      {
          headerName: "Company",
          headerTooltip: "Company",
          field:'organizationName',
          tooltipField: 'organizationName',
          cellRendererSelector: (params) => {
              if (this.isNotHistoricalOrg(params.data)) {
                  return {
                      component: ActionCellRendererComponent,
                      params: {
                          textField: 'organizationName',
                          action: this.showDetail(this),
                      }
                  }
              }
          },
          filter: "agTextColumnFilter",
          filterParams: this.formatterService.defaultTextFilterParams,
          minWidth: 150, 
          maxWidth: 250
      },
      {
          headerName: "Type",
          headerTooltip: "Type",
          field:'typeDescription',
          tooltipField: 'typeDescription',
          minWidth: 100,
          maxWidth: 150
      },
      {
          headerClass: "wrapped",
          headerName: "Owner Type",
          headerTooltip: "Owner Type",
          tooltipValueGetter: this.formatOwnerType(),
          valueGetter: this.formatOwnerType(),
          minWidth: 110,
          maxWidth: 200
      },
      {
          headerName: "ID",
          headerTooltip: "ID",
          field:'instrumentId',
          tooltipField: 'instrumentId',
          filter: "agTextColumnFilter",
          filterParams: this.formatterService.defaultTextFilterParams,
          minWidth: 100,
          maxWidth: 160
      },
      {
          headerName: 'Expires',
          headerTooltip: 'Expires',
          field: 'expirationDate',
          tooltipValueGetter: this.formatterService.formatDate('expirationDate'),
          valueFormatter: this.formatterService.formatDate('expirationDate'),
          comparator: dateComparator,
          filter: "agDateColumnFilter",
          filterParams: this.formatterService.defaultDateFilterParams,
          minWidth: 150,
          maxWidth: 160
      },
      {
          headerName:"Amount",
          field:'amount',
          tooltipValueGetter: this.formatterService.formatCurrency('amount'),
          filter: "agNumberColumnFilter",
          valueFormatter: this.formatterService.formatCurrency('amount'),
          minWidth: 125,
          maxWidth: 160
      }
  ];
  gridOptions: GridOptions;
	gridApi?: GridApi;
  private pendingRowData: ExpiringFinancialInstrumentListing[] | null = null;
  constructor(
      public financialAssuranceService: FinancialAssuranceService,
      private formatterService: GridFormatterService,
      private router: Router
  ) { }

  // Routes rowData updates through the grid API instead of the [rowData] Input binding.
  // Buffers until onGridReady fires so we never hand the grid data before its own initial
  // render pass has finished setting up - doing so mid-setup is what throws ag-grid error #252.
  private applyRowData(data: ExpiringFinancialInstrumentListing[]): void {
      if (this.gridApi != null) {
          setTimeout(() => this.gridApi?.setGridOption('rowData', data), 0);
      } else {
          this.pendingRowData = data;
      }
  }

  fetchData(): void {
      const dataSub = this.financialAssuranceService.getBondsExpiringSoon(this.timeframe).pipe(
          map(x => x.map(
              e => this.projectInstrument(e)
          ))
      ).subscribe(data => this.applyRowData(data));
      this.subscriptions.add(dataSub);
  }

  ngOnInit() {
      this.fetchData();
      this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.columnDefs);
	  this.gridOptions.onGridReady = (params) => {
	      this.gridApi = params.api;
	      if (this.pendingRowData != null) {
	          const data = this.pendingRowData;
	          this.pendingRowData = null;
	          this.applyRowData(data);
	      }
	  };
      this.gridOptions.headerHeight = 50;
      const routerSub = this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
          this.applyRowData([]);
      });
      this.subscriptions.add(routerSub);
  }

  ngOnDestroy() {
      this.subscriptions.unsubscribe();
  }

  resizeColumns(): void {
      if (this.gridApi != null) {
        setTimeout(() => {
            this.gridApi?.autoSizeAllColumns();
        }, 0);
      }
    }

    formatOwnerType(): any {
        return (obj: any) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["organizationId"] != null && this.complianceData != null) {
                let organizationId = obj.data["organizationId"];
                let compliance = this.complianceData.find(c => c.orgId == organizationId);
                if (compliance != null) {
                    if (compliance.isBrineHauler != null) {
                        if (compliance.isBrineHauler == true) returnValue = "Brine Hauler";
                    }
                    if (compliance.isWellOwner != null) {
                        if (compliance.isWellOwner == true) {
                            if (returnValue.length > 0) {
                                returnValue += ", ";
                            }
                            returnValue += "Well Owner";
                        }
                    }
                }

            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }

  getTypeDescription(code: FinancialInstrumentType): string {
      switch (code) {
          case FinancialInstrumentType.Cash : return "Cash";
          case FinancialInstrumentType.CD: return "CD";
          case FinancialInstrumentType.SuretyBond: return "Surety Bond";
          case FinancialInstrumentType.LetterOfCredit: return "Letter of Credit";
          case FinancialInstrumentType.FinancialStatement: return "Financial Statement";
          case FinancialInstrumentType.DomesticCashFee : return "Domestic Cash Fee";
          //case FinancialInstrumentType.Aggregate: return "Aggregate";
          //case FinancialInstrumentType.MinimumAmountCombinedFeesAndAggregate: return "Minimum Amount Combined";
      }
      return "";
    }

    showDetail(component: InternalUpcomingBondExpirationWidget) {
        return (obj: any) => {
            component.companyClicked.emit(obj.data.organizationId);
        }
    }

  projectInstrument(instrument: ExpiringFinancialInstrument): ExpiringFinancialInstrumentListing {

      let instrumentType = instrument.instrument.typeCode;
      let expiringFinancialinstrument = {
          id: instrument.instrument.id,
          organizationId: instrument.instrument.organizationId,
          organizationName: instrument.organizationName,
          amount: instrument.instrument.amount,
          instrumentId: instrument.instrument.instrumentId,
          guarantor: instrument.instrument.guarantor.name,
          typeCode: instrument.instrument.typeCode,
          typeDescription: this.getTypeDescription(instrument.instrument.typeCode),
          modifiedDate: instrument.instrument.modifiedDate
      } as ExpiringFinancialInstrumentListing;

      if (instrumentType == 'D')
          expiringFinancialinstrument.expirationDate = (instrument.instrument as CertificateOfDeposit).maturityDate;
      else
          expiringFinancialinstrument.expirationDate = instrument.instrument.expirationDate;

      return expiringFinancialinstrument;

    }

    public isNotHistoricalOrg(obj: any): boolean {
        let isHistorical = false;
        //Any org registered with RICS will have an Id starting with "ON"
        if (obj && obj.organizationId && obj.organizationId[0] != 'O' && ((obj.organizationId.length > 1 && obj.organizationId[1] != 'N') || obj.organizationId.length <= 1)) {
            isHistorical = true;
        }

        return !isHistorical;
    }
}
